import { inject, Injectable } from '@angular/core';
import { AxiosHttpService } from './axios-http.service';
import {
  SavePhoneRequest,
  SavePhoneResponse,
} from '../interfaces/dialogs/save-phone-dialog.model';
import { ApiResponse } from '../interfaces/api-response.model';
import { API_ENDPOINTS } from '../constants/api.constants';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { SavePhoneDialogComponent } from '../components/dialogs/save-phone-dialog/save-phone-dialog.component';
import { SavePhoneDialogModel } from '../interfaces/dialogs/save-phone-dialog.model';
import { SharedStorageService } from './shared-storage.service';
import { AuthService } from './auth.service';

/**
 * 會員中心保存電話號碼dialog service
 */
@Injectable({
  providedIn: 'root',
})
export class SavePhoneDialogService {
  axiosHttpService: AxiosHttpService = inject(AxiosHttpService);
  http: HttpClient = inject(HttpClient);
  private dialog: MatDialog = inject(MatDialog);
  private translateService: TranslateService = inject(TranslateService);
  private sharedStorageService: SharedStorageService =
    inject(SharedStorageService);
  private authService: AuthService = inject(AuthService);

  /**
   * 驗證 OTP 及儲存手機
   * @param request 儲存手機號碼請求參數
   */
  savePhone(
    request: SavePhoneRequest
  ): Observable<ApiResponse<SavePhoneResponse>> {
    return this.http.patch<ApiResponse<SavePhoneResponse>>(
      API_ENDPOINTS.SAVE_PHONE,
      request
    );
  }

  /**
   * 開啟綁定手機彈窗（SavePhoneDialogComponent）
   * @param data 彈窗參數（idNo、chartNo 等）
   * @returns 開啟的 dialog ref，呼叫端可自行接 afterClosed() 處理後續
   */
  openSavePhoneDialog(
    data: SavePhoneDialogModel
  ): MatDialogRef<SavePhoneDialogComponent> {
    return this.dialog.open(SavePhoneDialogComponent, {
      disableClose: true,
      autoFocus: false,
      data,
    });
  }

  /**
   * 若使用者尚未綁定手機（於彈窗點了「稍後填寫」），再次開啟綁定手機彈窗並回傳 true。
   * 呼叫端可用
   * `if (this.savePhoneDialogService.blockIfCellSaveLater(this.cellSaveLater, data)) return;`
   * 攔截後續動作，並自行接 afterClosed() 更新狀態。
   * @param cellSaveLater 稍後填寫標記，值為 'later' 時代表尚未綁定手機
   * @param data 綁定手機彈窗參數
   * @returns 開啟的 dialog ref；若無需攔截（未綁定標記不成立）則回傳 null
   */
  reopenIfCellSaveLater(
    cellSaveLater: string,
    data: SavePhoneDialogModel
  ): MatDialogRef<SavePhoneDialogComponent> | null {
    if (cellSaveLater === 'later'||this.sharedStorageService.get('createMemberRequest')?.cell) {
      return this.openSavePhoneDialog(data);
    }
    return null;
  }

  /**
   * 判斷已登入的使用者是否尚未綁定手機（storage 中 createMemberRequest.cell 為空）。
   * 未登入或已有手機皆視為不需攔截。
   * 注意：HIS 舊資料可能以 '0' 表示沒有手機，一併視為未綁定。
   * @returns 是否尚未綁定手機
   */
 isCellUnbound(): boolean {
  if (!this.authService.isLogin()) return false;

  const memberRequest = this.sharedStorageService.get('createMemberRequest');
  // 尚未載入到會員資料 → 無法判斷，先不攔截（避免新分頁誤跳）
  if (!memberRequest) return false;

  const cell = memberRequest.cell;
  return !cell || cell === '0';
}


  /**
   * 已登入但尚未綁定手機時，開啟綁定手機彈窗，彈窗關閉後執行原本要進行的動作。
   * 綁定成功會寫回手機再執行；若允許「稍後填寫」且使用者選擇稍後，也會繼續執行原動作（不寫回手機）。
   * 資料來源為 storage 的 createMemberRequest，適用於取不到 cellSaveLater 的共用元件。
   * 呼叫端可用
   * `if (this.savePhoneDialogService.requireCellBound(() => this.doSomething())) return;`
   * 攔截後續動作。
   * @param onBound 彈窗關閉後要執行的動作
   * @param hideLater 是否隱藏「稍後填寫」鈕（需強制完成綁定時傳 true，例如 reserveStep=6）
   * @param continueOnLater 使用者點「稍後填寫」時是否仍執行 onBound（預設 false = 稍後填寫不繼續）
   * @returns 是否已攔截（true = 已開綁定彈窗，呼叫端應中止後續流程）
   */
  requireCellBound(
    onBound: () => void,
    hideLater: boolean = false,
    continueOnLater: boolean = false
  ): boolean {
    if (!this.isCellUnbound()) {
      return false;
    }

    const memberRequest = this.sharedStorageService.get('createMemberRequest');
    const dialogRef = this.openSavePhoneDialog({
      isCreate: false,
      idNo: memberRequest?.idNo ?? '',
      chartNo: memberRequest?.chartNo ?? '',
      hideCancel: true,
      hideLater,
    });

    dialogRef.afterClosed().subscribe((result: string) => {
      // 點了「稍後填寫」→ 不寫回手機；依 continueOnLater 決定是否繼續原本流程
      if (result === 'later') {
        if (continueOnLater) {
          onBound();
        }
        return;
      }
      // 完全沒有結果（例如異常關閉）→ 不繼續
      if (!result) {
        return;
      }
      // 綁定成功，寫回 storage 的手機號碼
      const latestRequest = this.sharedStorageService.get(
        'createMemberRequest'
      );
      if (latestRequest) {
        latestRequest.cell = result;
        this.sharedStorageService.set('createMemberRequest', latestRequest);
      }
      // 綁定完成後執行原本要進行的動作
      onBound();
    });
    return true;
  }
}
